import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { ArrowRightLeft, AlertTriangle, CheckCircle, Lock } from 'lucide-react';

const CORE_FUNDS = [
  { id: 'A_FUND', name: 'A Fund — U.S. Treasury Securities', risk: 'Lowest' },
  { id: 'B_FUND', name: 'B Fund — Bloomberg U.S. Aggregate Bond', risk: 'Low' },
  { id: 'C_FUND', name: 'C Fund — S&P 500', risk: 'Medium' },
  { id: 'D_FUND', name: 'D Fund — DJ U.S. Completion TSM', risk: 'High' },
  { id: 'E_FUND', name: 'E Fund — MSCI EAFE International', risk: 'High' },
];

export const IFTPage = () => {
  const [ift, setIft] = useState({ used_this_month: 0, remaining: 2, limit: 2, g_fund_only: false });
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromFund, setFromFund] = useState('');
  const [toFund, setToFund] = useState('');
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const tspRes = await api.get('/tsp/summary');
      setIft(tspRes.data?.ift || {});
      try {
        const pRes = await api.get('/investments/portfolio');
        setHoldings(pRes.data?.holdings || pRes.data?.portfolio || []);
      } catch {}
    } catch {} finally { setLoading(false); }
  };

  const handleTransfer = async () => {
    setError(''); setSuccess('');
    if (!fromFund || !toFund) { setError('Select both from and to funds'); return; }
    if (fromFund === toFund) { setError('From and To funds must be different'); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setError('Enter a valid amount'); return; }
    if (ift.g_fund_only && toFund !== 'A_FUND') { setError('After 2 IFTs this month, transfers are restricted to A Fund only (Recordkeeping Bulletin 08-6)'); return; }
    if (ift.remaining <= 0 && toFund !== 'A_FUND') { setError('Monthly IFT limit reached. Only A Fund transfers permitted.'); return; }
    setSubmitting(true);
    // Simulate IFT - in production this would call a real endpoint
    setTimeout(() => {
      setSuccess(`Transfer of $${amt.toLocaleString()} from ${fromFund} to ${toFund} submitted successfully.`);
      setIft(prev => ({ ...prev, used_this_month: prev.used_this_month + 1, remaining: Math.max(prev.remaining - 1, 0), g_fund_only: prev.used_this_month + 1 >= 2 }));
      setAmount(''); setFromFund(''); setToFund('');
      setSubmitting(false);
    }, 1000);
  };

  const selectClass = "w-full h-10 px-3 bg-white border border-[#D1D5DB] rounded text-sm text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20";

  return (
    <div className="space-y-4" data-testid="ift-page">
      <h1 className="text-lg font-semibold text-[#1B2A4A]" style={{ fontFamily: 'IBM Plex Sans' }}>Interfund Transfers (IFT)</h1>

      {/* Rules */}
      <div className="panel p-4 bg-blue-50 border-blue-200">
        <span className="text-xs font-semibold text-blue-800 uppercase tracking-wider">Recordkeeping Bulletin 08-6 — IFT Rules</span>
        <div className="mt-2 text-xs text-blue-700 space-y-1">
          <p>2 unrestricted Interfund Transfers per calendar month.</p>
          <p>After 2nd IFT, only transfers INTO A Fund are permitted until the next month.</p>
        </div>
      </div>

      {/* IFT Status */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="panel p-4">
          <span className="overline">IFTs Used This Month</span>
          <div className="font-mono text-2xl font-semibold text-[#1B2A4A] mt-1" data-testid="ift-used">{ift.used_this_month}/{ift.limit}</div>
        </div>
        <div className="panel p-4">
          <span className="overline">Remaining</span>
          <div className={`font-mono text-2xl font-semibold mt-1 ${ift.remaining > 0 ? 'text-emerald-600' : 'text-amber-600'}`} data-testid="ift-remaining">{ift.remaining}</div>
        </div>
        <div className="panel p-4">
          <span className="overline">Status</span>
          <div className="mt-1 flex items-center gap-2">
            {ift.g_fund_only ? (
              <><Lock size={16} className="text-amber-500" /><span className="text-sm font-medium text-amber-700">A Fund Only</span></>
            ) : (
              <><CheckCircle size={16} className="text-emerald-600" /><span className="text-sm font-medium text-emerald-700">Unrestricted</span></>
            )}
          </div>
        </div>
      </div>

      {/* Transfer Form */}
      <div className="panel p-5" data-testid="ift-form">
        <span className="overline mb-4 block">New Transfer</span>
        {error && <div className="mb-3 flex items-center gap-2 bg-red-50 border border-red-200 rounded px-3 py-2"><AlertTriangle size={14} className="text-red-600" /><span className="text-xs text-red-700">{error}</span></div>}
        {success && <div className="mb-3 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-3 py-2"><CheckCircle size={14} className="text-emerald-600" /><span className="text-xs text-emerald-700">{success}</span></div>}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-[#1B2A4A] mb-1">From Fund</label>
            <select value={fromFund} onChange={e => setFromFund(e.target.value)} className={selectClass} data-testid="ift-from-select">
              <option value="">Select fund...</option>
              {CORE_FUNDS.map(f => <option key={f.id} value={f.id}>{f.id.replace('_', ' ')}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#1B2A4A] mb-1">To Fund {ift.g_fund_only && '(A Fund only)'}</label>
            <select value={toFund} onChange={e => setToFund(e.target.value)} className={selectClass} data-testid="ift-to-select">
              <option value="">Select fund...</option>
              {ift.g_fund_only ? <option value="A_FUND">A Fund</option> : CORE_FUNDS.map(f => <option key={f.id} value={f.id}>{f.id.replace('_', ' ')}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#1B2A4A] mb-1">Amount ($)</label>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="1000" className={selectClass} data-testid="ift-amount-input" />
          </div>
        </div>
        <button onClick={handleTransfer} disabled={submitting} data-testid="submit-ift-btn" className="mt-4 flex items-center gap-2 px-4 py-2 bg-[#1B2A4A] text-white text-sm rounded hover:bg-[#2D3F5F] disabled:opacity-50 transition-colors">
          <ArrowRightLeft size={14} />{submitting ? 'Processing...' : 'Submit Transfer'}
        </button>
      </div>

      {/* Current Holdings */}
      {holdings.length > 0 && (
        <div className="panel p-5">
          <span className="overline mb-3 block">Current Fund Holdings</span>
          <div className="space-y-2">
            {holdings.map((h, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-[#F0F1F3] last:border-0">
                <span className="text-sm font-mono font-medium text-[#1B2A4A]">{h.symbol || h.fund_id}</span>
                <div className="text-right">
                  <span className="text-sm font-mono text-[#1B2A4A]">{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(h.market_value || h.cost_basis || 0)}</span>
                  <span className="text-xs text-[#9CA3AF] ml-2">{h.shares?.toFixed(2)} shares</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
